import { useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { KeyRound, Monitor, ShieldCheck, UserCog } from "lucide-react";

const LANGS = ["de", "en", "es", "pt", "it"];

export default function MyProfile() {
  const { t, user, setUser } = useApp();
  const [form, setForm] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [sessions, setSessions] = useState([]);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "",
      job_title: user.job_title || "", department: user.department || "", language: user.language || "de" });
    api.get(`/iam/users/${user.id}/sessions`).then((r) => setSessions(r.data || [])).catch(() => {});
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/iam/me/profile", form);
      setUser({ ...user, ...data });
      toast.success(t("prof_saved"));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) { toast.error(t("prof_pw_mismatch")); return; }
    try {
      await api.post("/auth/password/change", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("✓");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errDetail(e)); }
  };

  const startMfa = async () => {
    const { data } = await api.post("/auth/mfa/setup", {});
    setMfa(data);
  };
  const activateMfa = async () => {
    try {
      await api.post("/auth/mfa/activate", { code });
      toast.success("✓"); setMfa(null); setCode("");
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="my-profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A] flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
          <UserCog className="h-6 w-6 text-[#E8871E]" />{t("my_profile")}
        </h1>
        <p className="text-xs text-gray-500">{user?.email}</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[["first_name", "first_name"], ["last_name", "last_name"], ["phone", "iam_phone"],
          ["job_title", "iam_job_title"], ["department", "iam_department"]].map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{t(label)}</Label>
            <Input value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              data-testid={`profile-${k}-input`} />
          </div>
        ))}
        <div className="space-y-1">
          <Label className="text-xs">{t("iam_language")}</Label>
          <Select value={form.language || "de"} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
            <SelectTrigger data-testid="profile-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving} onClick={save}
            data-testid="profile-save-btn">{t("save")}</Button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-gray-400" />{t("prof_change_password")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input type="password" placeholder={t("prof_current_pw")} value={pw.current_password}
            onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="profile-current-pw" />
          <Input type="password" placeholder={t("prof_new_pw")} value={pw.new_password}
            onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="profile-new-pw" />
          <Input type="password" placeholder={t("prof_confirm_pw")} value={pw.confirm}
            onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="profile-confirm-pw" />
        </div>
        <Button variant="outline" size="sm" onClick={changePw} data-testid="profile-change-pw-btn">{t("save")}</Button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" data-testid="profile-mfa-card">
        <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-gray-400" />{t("prof_mfa_setup")}
          {user?.mfa_enabled && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-inherit text-[10px]">{t("iam_mfa_enabled")}</Badge>}
        </p>
        {!user?.mfa_enabled && !mfa && (
          <Button variant="outline" size="sm" onClick={startMfa} data-testid="profile-mfa-start-btn">{t("prof_mfa_setup")}</Button>
        )}
        {mfa && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">{t("prof_mfa_scan")}</p>
            <code className="block text-[11px] bg-gray-50 border border-gray-100 rounded px-2 py-1.5 break-all" data-testid="profile-mfa-secret">
              {mfa.secret}
            </code>
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="max-w-[140px]"
                data-testid="profile-mfa-code-input" />
              <Button className="bg-[#E8871E] hover:bg-[#d1751a] text-white" onClick={activateMfa}
                data-testid="profile-mfa-activate-btn">{t("prof_mfa_activate")}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50" data-testid="profile-sessions">
        <p className="px-4 py-2.5 text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5">
          <Monitor className="h-4 w-4 text-gray-400" />{t("prof_sessions")}
        </p>
        {sessions.map((s) => (
          <div key={s.id} className="px-4 py-2 text-xs flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{s.method}</Badge>
            <span className="font-mono">{s.ip || "–"}</span>
            <span className="text-gray-400 truncate max-w-[200px]">{s.user_agent}</span>
            {s.revoked ? <Badge className="bg-gray-100 text-gray-500 hover:bg-inherit text-[10px] ml-auto">{s.revoked_reason}</Badge>
              : <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]" data-testid={`profile-revoke-${s.id}`}
                  onClick={async () => { await api.delete(`/iam/sessions/${s.id}`); const r = await api.get(`/iam/users/${user.id}/sessions`); setSessions(r.data); }}>
                  {t("iam_revoke")}
                </Button>}
          </div>
        ))}
        {!sessions.length && <p className="px-4 py-3 text-xs text-gray-400">–</p>}
      </div>
    </div>
  );
}
